import React, { useState, useContext, memo, useEffect } from 'react';
import { shape, string, number, func } from 'prop-types';
import { useIntl } from 'react-intl';
// eslint-disable-next-line import/no-extraneous-dependencies
import InfiniteScroll from 'react-infinite-scroll-component';
import { DataContext } from '../../containers/Search/context';
import messages from './messages';
import { devices, getDeviceForWidth } from '../../utils/devices';
import Table from './Table';
import List from './List';

/**
 * Alternative implementations can be with these projects:
 *   - react-virtualized
 *   - react-base-table
 *   - react-window
 *   - window-table
 *   - react-fluid-table
 */
const Results = ({ filters, amount, setResultCount }) => {
  const intl = useIntl();
  const api = useContext(DataContext);
  const { formatMessage, locale } = intl;

  const [device, setDevice] = useState(devices.mobile);
  const [jobs, setJobs] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [tracker, setTracker] = useState(() => ({
    current: jobs.slice(0, amount),
    prev: 0,
    next: amount,
  }));

  const { current, prev, next } = tracker;

  useEffect(() => {
    // When filters change, we need to reset everything.
    const jobData = api.getJobs(filters);
    setJobs(jobData);
    setHasMore(jobData.length > amount);
    setTracker({
      current: jobData.slice(0, amount),
      prev: 0,
      next: amount,
    });

    setResultCount(jobData.length);

    // @todo bring this to some HOC maybe?
    setDevice(getDeviceForWidth(window.innerWidth));
  }, [filters]);

  const getMoreData = () => {
    if (current.length === jobs.length) {
      setHasMore(false);
      return;
    }

    setTracker({
      current: current.concat(jobs.slice(prev + amount, next + amount)),
      prev: prev + amount,
      next: next + amount,
    });
  };

  if (jobs.length === 0) {
    if (locale !== 'zh-CN' && filters.site && filters.site === 'CN') {
      return (
        <div>
          <div>{formatMessage(messages.NoResults)}</div>
          {messages.NoResultsWithLinkToCN && (
            <p
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: formatMessage(messages.NoResultsWithLinkToCN) }}
              className="tds-text--h6 tds-text--400"
            />
          )}
        </div>
      );
    }
    return <div>{formatMessage(messages.NoResults)}</div>;
  }

  const isMobile = device === devices.mobile;

  return (
    <InfiniteScroll
      dataLength={current.length}
      next={getMoreData}
      hasMore={hasMore}
      loader={<h5>{formatMessage(messages.Loading)}...</h5>}
    >
      {isMobile ? <List results={current} /> : <Table results={current} />}
    </InfiniteScroll>
  );
};

Results.propTypes = {
  filters: shape({
    query: string,
    department: string,
    region: string,
    site: string, // country
    location: string,
  }),
  amount: number,
  setResultCount: func.isRequired,
};

Results.defaultProps = {
  filters: {},
  amount: 50,
};

export default memo(Results);
